import { DonutChart } from '@ssc-ui/charts-react';
import { DonutChartConfig } from '@ssc-ui/charts-react/es/components/donut-chart/types';
import cls from 'classnames';
import React from 'react';
import { ProFilter } from 'react-pro-components';
import { Form, Skeleton, Table } from 'ssc-ui-react';
import { SorterResult } from 'ssc-ui-react/es/components/table/types';
import { ColumnProps } from 'ssc-ui-react/typings/components/table/types';

import { ReactComponent as ExceptionIcon } from '@/assets/svg/exception.svg';
import { $gt } from '@/common/appCtx';
import { OperatorOptionItem, WorkstationOptionItem } from '@/pages/workstation-dashboard/defined';
import useDropdownListStore from '@/pages/workstation-dashboard/store/use-dropdown-list';
import useLastHourOperatorPerformanceStore from '@/pages/workstation-dashboard/store/use-last-hour-operator-performance';
import useWorkstationOverviewStore from '@/pages/workstation-dashboard/store/use-workstation-overview';
import { dashboardDBManager } from '@/pages/workstation-dashboard/utils/storage';

import { CacheType, DEFAULT_PAGE_SIZE, MeetTargetOptions } from '../../../const';
import styles from '../index.module.less';

type ColumnType = ColumnProps<{
  hourly_productivity?: number;
  manhour?: number;
  manpower_check_in?: number;
  target?: number;
  throughput?: number;
  workstation?: string;
  meet_target?: number;
}>

type FormType = {
  operator_id_list?: string[];
  biz_workstation_id_list?: string[];
  select_meet_target?: number;
}

type StorageType = {
  operator_list: OperatorOptionItem[];
  biz_workstation_list: WorkstationOptionItem[];
  select_meet_target: number;
}

const defaultSorterModel = {
  hourly_productivity: undefined,
  manhour: undefined,
  target: undefined,
};

const OperatorPerformanceLastHour: React.FC = () => {
  const [form] = Form.useForm();
  const sorterModelRef = React.useRef(defaultSorterModel);
  const [sorterModel, setSorterModel] = React.useState(defaultSorterModel);
  const targetGroup = useWorkstationOverviewStore.use.targetGroup();
  const workstationDropdownList = useDropdownListStore.use.lastHourWorkstationDropdownList();
  const workstationDropdownListMap = useDropdownListStore.use.lastHourWorkstationDropdownListMap();
  const workstationDropdownListLoading = useDropdownListStore.use.lastHourWorkstationDropdownListLoading();
  const setWorkstationDropdownList = useDropdownListStore.use.setLastHourWorkstationDropdownList();
  const operatorDropdownList = useDropdownListStore.use.lastHourOperatorDropdownList();
  const operatorDropdownListMap = useDropdownListStore.use.lastHourOperatorDropdownListMap();
  const operatorDropdownListLoading = useDropdownListStore.use.lastHourOperatorDropdownListLoading();
  const setOperatorDropdownList = useDropdownListStore.use.setLastHourOperatorDropdownList();
  const pagination = useLastHourOperatorPerformanceStore.use.pagination();
  const setPagination = useLastHourOperatorPerformanceStore.use.setPagination();
  const loading = useLastHourOperatorPerformanceStore.use.loading();
  const tableData = useLastHourOperatorPerformanceStore.use.items();
  const totalInfo = useLastHourOperatorPerformanceStore.use.totalInfo();
  const fetchTableData = useLastHourOperatorPerformanceStore.use.getOperatorPerformanceLastHourAsync();
  const setLastHourForm = useLastHourOperatorPerformanceStore.use.setLastHourForm();

  const getCache = React.useCallback(async () => {
    try {
      const cache = await dashboardDBManager.get(CacheType.OperatorPerformanceLast1Hour) as StorageType | undefined;
      const selectMeetTarget = cache?.select_meet_target;
      const disabledOperatorList: OperatorOptionItem[] = [];
      const disabledWorkstationList: WorkstationOptionItem[] = [];
      const operatorIdList: string[] = [];
      const bizWorkstationIdList: string[] = [];

      cache?.operator_list?.forEach(item => {
        if (!operatorDropdownListMap[item.value]) {
          disabledOperatorList.push({ ...item, disabled: true });
        }
        operatorIdList.push(item.value);
      });
      cache?.biz_workstation_list?.forEach(item => {
        if (!workstationDropdownListMap[item.value]) {
          disabledWorkstationList.push({ ...item, disabled: true });
        }
        bizWorkstationIdList.push(item.value);
      });
      setWorkstationDropdownList(disabledWorkstationList, true);
      setOperatorDropdownList(disabledOperatorList, true);
      form.setFieldValue('operator_id_list', operatorIdList.length ? operatorIdList : undefined);
      form.setFieldValue('biz_workstation_id_list', bizWorkstationIdList.length ? bizWorkstationIdList : undefined);
      form.setFieldValue('select_meet_target', selectMeetTarget);
    } catch (e) {
      console.error('[OperatorPerformanceLastHour] getCache error: ', e);
    } finally {
      setLastHourForm(form);
    }
  }, [form, setOperatorDropdownList, setWorkstationDropdownList, operatorDropdownListMap, workstationDropdownListMap, setLastHourForm]);

  const setCache = React.useCallback(async (values: FormType) => {
    dashboardDBManager.put(CacheType.OperatorPerformanceLast1Hour, {
      operator_list: values.operator_id_list?.map(item => {
        return {
          value: item,
          label: operatorDropdownListMap[item],
        };
      }),
      biz_workstation_list: values.biz_workstation_id_list?.map(item => {
        return {
          value: item,
          label: workstationDropdownListMap[item],
        };
      }),
      select_meet_target: values.select_meet_target,
    });
  }, [operatorDropdownListMap, workstationDropdownListMap]);

  React.useLayoutEffect(() => {
    getCache();
  }, [getCache]);

  const tableColumns = React.useMemo(() => {
    return [
      {
        title: $gt('Operator'),
        dataIndex: 'operator',
        ellipsis: true,
        lineClamp: 1,
      },
      {
        title: $gt('Workstation'),
        dataIndex: 'workstation',
        ellipsis: true,
        lineClamp: 1,
      },
      {
        title: $gt('Hourly Productivity (Orders/Mnhr)'),
        dataIndex: 'hourly_productivity',
        sorter: {
          key: 'hourly_productivity',
          value: sorterModel.hourly_productivity,
          directions: ['ascend', 'descend'],
        },
        width: 160,
        ellipsis: true,
        lineClamp: 1,
      },
      {
        title: $gt('Manhour'),
        dataIndex: 'manhour',
        sorter: {
          key: 'manhour',
          value: sorterModel.manhour,
          directions: ['ascend', 'descend'],
        },
        width: 100,
        ellipsis: true,
        lineClamp: 1,
      },
      {
        title: $gt('Throughput/Target (Orders/Hour)'),
        dataIndex: 'target',
        ellipsis: true,
        lineClamp: 1,
        sorter: {
          key: 'target',
          value: sorterModel.target,
          directions: ['ascend', 'descend'],
        },
        width: 136,
        render: (value: number, record) => {
          const showException = Number(record.throughput) < Number(record.target);
          return (
            <div className={styles.throughputTarget}>
              <div className={cls({ [styles.warn]: showException })}>
                <span className={styles.left}>{record.throughput}</span> / <span className={styles.right}>{value}</span>
              </div>
              {showException && (
                <ExceptionIcon />
              )}
            </div>
          );
        }
      }
    ] as ColumnType[];
  }, [sorterModel.hourly_productivity, sorterModel.manhour, sorterModel.target]);

  const donutChartConfig = React.useMemo(() => {
    const { total_count, on_target_count, below_target_count } = totalInfo || {};
    const onTargetRate = total_count ? (on_target_count || 0) / total_count * 100 : 0;
    const belowTargetRate = total_count ? (below_target_count || 0) / total_count * 100 : 0;
    return {
      seriesField: 'type',
      valueField: 'value',
      dataLabel: { show: false },
      contentText: `{title|${$gt('Total Operators')}}\n{count|${total_count || 0}}`,
      highlight: true,
      diameter: 200,
      legend: {
        show: true,
        position: 'bottomCenter',
        formatter: item => {
          return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', marginRight: 40 }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                <span style={{ width: 8, height: '8px', borderRadius: '50%', display: 'inline-block', marginRight: 8, backgroundColor: item.color, }} />
                <span>{item.name}</span>
              </div>
              {item.name === $gt('On Target') ? (
                <div>
                  <span style={{ fontWeight: 500, fontSize: 22, color: '#303844' }}>{on_target_count || 0}</span>
                  <span style={{ fontSize: 14, color: '#959BA4' }}>/{onTargetRate.toFixed(1)}%</span>
                </div>
              ) : (
                <div>
                  <span style={{ fontWeight: 500, fontSize: 22, color: '#303844' }}>{below_target_count || 0}</span>
                  <span style={{ fontSize: 14, color: '#959BA4' }}>/{belowTargetRate.toFixed(1)}%</span>
                </div>
              )}
            </div>
          );
        }
      },
    } as DonutChartConfig;
  }, [totalInfo]);

  const donutChartData = React.useMemo(() => {
    return [
      { type: $gt('On Target'), value: totalInfo?.on_target_count },
      { type: $gt('Below Target'), value: totalInfo?.below_target_count },
    ];
  }, [totalInfo?.below_target_count, totalInfo?.on_target_count]);

  return (
    <div>
      <div className={styles.subTitle}>
        {$gt('Operator Performance')}
      </div>
      <ProFilter
        formProps={{ form }}
        fields={[
          {
            label: $gt('Operator ID/Name'),
            type: 'select',
            name: 'operator_id_list',
            ctrlProps: {
              mode: 'multiple',
              maxTagCount: 1,
              maxTagTextWidth: 80,
              allowClear: true,
              filterOption: true,
              loading: operatorDropdownListLoading,
              options: operatorDropdownList,
            }
          },
          {
            label: $gt('Workstation ID/Name'),
            type: 'select',
            name: 'biz_workstation_id_list',
            ctrlProps: {
              mode: 'multiple',
              maxTagCount: 1,
              maxTagTextWidth: 80,
              filterOption: true,
              allowClear: true,
              loading: workstationDropdownListLoading,
              options: workstationDropdownList,
            }
          },
          {
            label: $gt('Meet Target'),
            type: 'select',
            name: 'select_meet_target',
            ctrlProps: {
              allowClear: true,
              options: MeetTargetOptions
            }
          }
        ]}
        onChange={(model) => {
          setCache(model);
          fetchTableData({ workstation_group_id: targetGroup?.groupId });
        }}
      />
      <div className={styles.chartArea}>
        <div className={styles.chart}>
          {loading ? (
            <Skeleton.Chart />
          ) : (
            <DonutChart
              dataSource={donutChartData}
              valueFormatter={{
                percent: (value: number) => ({
                  toHTML: () => `${value.toFixed(1)}%`,
                }),
              }}
              config={donutChartConfig}
            />
          )}
        </div>
        <Table
          className={styles.table}
          dataSource={tableData}
          columns={tableColumns}
          scroll={{ y: 192 }}
          loading={loading}
          pagination={pagination}
          onChange={(pagination, filters, sorter) => {
            const field = (sorter as SorterResult<Record<string, any>>).field;
            const order = (sorter as SorterResult<Record<string, any>>).order;
            const newSorterModel = { ...defaultSorterModel, [field as string]: order };
            sorterModelRef.current = newSorterModel;
            setSorterModel(newSorterModel);
            setPagination({
              current: pagination.current || 1,
              pageSize: pagination.pageSize || DEFAULT_PAGE_SIZE,
              total: pagination.total || 0,
            });
            fetchTableData({ workstation_group_id: targetGroup?.groupId });
          }}
        />
      </div>
    </div>
  );
};

export default OperatorPerformanceLastHour;
